import { useState } from 'react';
import { Pencil, Trash2, MoreVertical } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatAmount } from '@/contabilidad/utils/formatters';
import type { Movimiento } from '@/contabilidad/types';
import { SECTION_LABEL_CLASS } from '@/contabilidad/constants/ui';
import { TipoBadge } from './TipoBadge';
import { CajaBadge } from './CajaBadge';

interface MovimientosTableProps {
  movimientos: Movimiento[];
  showPagination?: boolean;
  onEdit?: (m: Movimiento) => void;
  onDelete?: (m: Movimiento) => void;
}

function MontoCell({ m }: { m: Movimiento }) {
  const isIngreso = m.tipo === 'Ingreso';
  const isNegative = m.monto < 0;
  const formatted = formatAmount(Math.abs(m.monto), m.moneda);
  return (
    <span className={`text-sm font-semibold tabular-nums whitespace-nowrap ${isIngreso ? 'text-blue-600' : 'text-slate-600'}`}>
      {m.moneda} {isNegative ? '-' : ''}$ {formatted}
    </span>
  );
}

function RowActions({ m, onEdit, onDelete }: { m: Movimiento; onEdit?: (m: Movimiento) => void; onDelete?: (m: Movimiento) => void }) {
  const [open, setOpen] = useState(false);

  const handleAction = (callback: () => void) => {
    setOpen(false);
    setTimeout(callback, 100);
  };

  return (
    <TableCell>
      <div className="flex justify-end">
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Abrir menú de acciones</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit && (
              <DropdownMenuItem onClick={() => handleAction(() => onEdit(m))}>
                <Pencil className="h-4 w-4" />
                Editar
              </DropdownMenuItem>
            )}
            {onEdit && onDelete && <DropdownMenuSeparator />}
            {onDelete && (
              <DropdownMenuItem
                className="text-gray-900 focus:text-gray-900"
                onClick={() => handleAction(() => onDelete(m))}
              >
                <Trash2 className="h-4 w-4" />
                Eliminar
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TableCell>
  );
}

export function MovimientosTable({ movimientos, showPagination = false, onEdit, onDelete }: MovimientosTableProps) {
  const hasActions = !!(onEdit || onDelete);
  const colSpan = hasActions ? 7 : 6;

  return (
    <div>
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className={SECTION_LABEL_CLASS}>Fecha</TableHead>
            <TableHead className={SECTION_LABEL_CLASS}>Tipo</TableHead>
            <TableHead className={SECTION_LABEL_CLASS}>Categoría</TableHead>
            <TableHead className={SECTION_LABEL_CLASS}>Descripción</TableHead>
            <TableHead className={SECTION_LABEL_CLASS}>Caja</TableHead>
            <TableHead className={`${SECTION_LABEL_CLASS} text-right`}>Monto</TableHead>
            {hasActions && <TableHead className={SECTION_LABEL_CLASS} />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {movimientos.length === 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} className="text-center text-muted-foreground py-8">
                No hay movimientos para mostrar.
              </TableCell>
            </TableRow>
          )}
          {movimientos.map((m) => (
            <TableRow key={m.id}>
              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{m.fecha}</TableCell>
              <TableCell>
                <TipoBadge tipo={m.tipo} />
              </TableCell>
              <TableCell className="whitespace-normal text-sm text-muted-foreground">
                {m.categoria}
                {m.fondo === 'certificaciones_poderes' && (
                  <span className="ml-1.5 inline-block rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 whitespace-nowrap">
                    Cert. y poderes
                  </span>
                )}
              </TableCell>
              <TableCell className="min-w-[14rem] whitespace-normal break-words text-sm font-medium">{m.descripcion}</TableCell>
              <TableCell>
                <CajaBadge caja={m.caja} />
              </TableCell>
              <TableCell className="text-right">
                <MontoCell m={m} />
              </TableCell>
              {hasActions && <RowActions m={m} onEdit={onEdit} onDelete={onDelete} />}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {showPagination && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-3 border-t mt-2">
          <span className="text-sm text-muted-foreground">Mostrando {movimientos.length} registros</span>
          <div className="flex gap-2">
            <button className="px-3 py-1 text-sm border rounded-md text-muted-foreground disabled:opacity-50" disabled>Anterior</button>
            <button className="px-3 py-1 text-sm border rounded-md text-muted-foreground disabled:opacity-50" disabled>Siguiente</button>
          </div>
        </div>
      )}
    </div>
  );
}
